import React from "react";
import { cn, formatNumber } from "@/lib/utils";
import { Wrench, AlertTriangle, ShieldCheck, Flame, Activity } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export interface MoldWearBarProps {
  usageCount?: number | null;
  maxImpressions?: number | null;
  maintenanceInterval?: number | null;
  lastMaintenanceImpressions?: number | null;
  compact?: boolean;
  showText?: boolean;
  showMaintenanceAlert?: boolean;
  className?: string;
}

export function MoldWearBar({
  usageCount = 0,
  maxImpressions = 100000,
  maintenanceInterval = 25000,
  lastMaintenanceImpressions = 0,
  compact = false,
  showText = true,
  showMaintenanceAlert = true,
  className,
}: MoldWearBarProps) {
  const cur = Number(usageCount) || 0;
  const max = Number(maxImpressions) > 0 ? Number(maxImpressions) : 100000;
  const maintInt = Number(maintenanceInterval) > 0 ? Number(maintenanceInterval) : 25000;
  const lastMaint = Number(lastMaintenanceImpressions) || 0;
  const sinceMaint = Math.max(0, cur - lastMaint);
  const wearPct = Math.min(100, Math.round((cur / max) * 100));
  const needsMaintenance = sinceMaint >= maintInt;
  const criticalWear = wearPct >= 85;

  const barColor =
    wearPct >= 85
      ? "bg-rose-500"
      : wearPct >= 70
      ? "bg-amber-500"
      : "bg-emerald-500";

  const textColor =
    wearPct >= 85
      ? "text-rose-700"
      : wearPct >= 70
      ? "text-amber-700"
      : "text-emerald-700";

  const badgeBg =
    wearPct >= 85
      ? "bg-rose-100 text-rose-800 border-rose-200"
      : wearPct >= 70
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-emerald-100 text-emerald-800 border-emerald-200";

  if (compact) {
    return (
      <div className={cn("min-w-[110px] space-y-1 text-right", className)}>
        {showText && (
          <div className="flex items-center justify-end gap-1.5 text-[11px] font-mono leading-tight">
            <span className="font-bold text-slate-800">
              {formatNumber(cur)} <span className="font-normal text-slate-400">/ {formatNumber(max)}</span>
            </span>
            <span className={cn("text-[10px] font-bold px-1 py-0.2 rounded font-mono", badgeBg)}>
              {wearPct}%
            </span>
          </div>
        )}
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all duration-300", barColor)}
            style={{ width: `${wearPct}%` }}
          />
        </div>
        {showMaintenanceAlert && (
          <div className="flex items-center justify-end gap-1 text-[9px] font-medium leading-none">
            {needsMaintenance ? (
              <span className="text-amber-700 font-bold flex items-center gap-0.5">
                <Wrench className="w-2.5 h-2.5 text-amber-600 animate-pulse" />
                Cần bảo dưỡng
              </span>
            ) : criticalWear ? (
              <span className="text-rose-700 font-bold flex items-center gap-0.5">
                <Flame className="w-2.5 h-2.5 text-rose-600" />
                Sắp mòn dao
              </span>
            ) : (
              <span className="text-slate-400">
                BD sau: {formatNumber(Math.max(0, maintInt - sinceMaint))}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full detailed display (for Detail View, Summary Cards)
  return (
    <div className={cn("space-y-2 p-3 bg-white border border-slate-200 rounded-xl shadow-xs", className)}>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5 font-medium text-slate-700">
          <Activity className="w-4 h-4 text-indigo-600" />
          <span>Hao mòn dao &amp; Tuổi thọ khuôn</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={cn("text-xs font-bold font-mono px-2 py-0.5 rounded-full border", badgeBg)}>
            {wearPct}% mòn dao
          </span>
          {needsMaintenance ? (
            <Badge className="bg-amber-500 hover:bg-amber-600 text-white text-[10px] py-0.5 px-2 animate-pulse flex items-center gap-1">
              <Wrench className="w-3 h-3" /> Cần mài dao
            </Badge>
          ) : criticalWear ? (
            <Badge className="bg-rose-600 hover:bg-rose-700 text-white text-[10px] py-0.5 px-2 flex items-center gap-1">
              <Flame className="w-3 h-3" /> Cảnh báo mòn cao
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] py-0.5 px-2 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Dao hoạt động tốt
            </Badge>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden p-0.5 border border-slate-100">
          <div
            className={cn("h-full rounded-full transition-all duration-300", barColor)}
            style={{ width: `${wearPct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span>0 lượt</span>
          <span className="font-mono font-bold text-slate-900">
            {formatNumber(cur)} / {formatNumber(max)} lượt dập
          </span>
          <span>{formatNumber(max)} lượt</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-100 text-xs">
        <div>
          <span className="text-slate-400 block text-[10px]">Chu kỳ bảo dưỡng / mài dao:</span>
          <span className="font-mono font-semibold text-slate-700">
            {formatNumber(sinceMaint)} / {formatNumber(maintInt)} lượt
          </span>
        </div>
        <div className="text-right">
          <span className="text-slate-400 block text-[10px]">Lần bảo dưỡng gần nhất:</span>
          <span className="font-mono font-semibold text-slate-700">
            {lastMaint > 0 ? `${formatNumber(lastMaint)} lượt` : "Chưa bảo dưỡng"}
          </span>
        </div>
      </div>
    </div>
  );
}
